import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import UserRole from '@careerstart/wae-common/src/main/constants/user-role';
import LanguageConverter from '@careerstart/wae-common/src/main/helperFunction/LanguageConverter';
import LanguageConverterV2 from '@careerstart/wae-common/src/main/helperFunction/LanguageConverterV2';
import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  CircularProgress,
  IconButton,
  Modal,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';

import asyncService from '../../../datahub/asyncService';
import { dispatchErrors } from '../../../datahub/errorUtils';
import WaeButton, { BUTTON_VARIANT } from '../../components/Button';
import selectUser from '../../store/selectors/appSelector';
import { ACTION_BLUE, BLACK, PRIMARY_PURPLE } from '../../theme/colorConstants';
import { PRIMARY_FONT } from '../../theme/fontConstants';
import { epochToDateInReadableFormat, epochToTimeInReadableFormat } from '../../utils';
import { showSnackbar } from '../app';

const TARGET = {
  JOB_ORDERS: 'jobOrders',
  CORPORATIONS: 'corporations',
};

const USER_SEARCH_DEBOUNCE_MS = 750;

const corporationIdOf = (jobOrder) => {
  const corporation = jobOrder?.corporation;
  if (!corporation) return null;
  if (typeof corporation === 'string') return corporation;
  return corporation._id || corporation.id || null;
};

const uniqueCorporations = (jobOrders) => {
  const byId = new Map();
  jobOrders.forEach((jobOrder) => {
    const id = corporationIdOf(jobOrder);
    if (!id || byId.has(String(id))) return;
    const name = typeof jobOrder.corporation === 'object' ? jobOrder.corporation.name : '';
    byId.set(String(id), { id: String(id), name: name || '' });
  });
  return [...byId.values()];
};

const buildUserSearchBody = (searchTerm) => {
  const filters = [
    {
      operation: 'includedIn',
      field: 'role',
      value: [UserRole.ADMIN, UserRole.RECRUITER],
    },
  ];
  const trimmed = searchTerm.trim();
  if (trimmed) {
    filters.push({ operation: 'icontains', field: 'name', value: trimmed });
  }
  return { filters };
};

const styles = {
  modal: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 720,
    maxWidth: '92vw',
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column',
    bgcolor: 'background.paper',
    borderRadius: 2,
    boxShadow: 24,
    p: 3,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    mb: 2,
  },
  title: {
    fontFamily: PRIMARY_FONT[700],
    fontSize: '18px',
    color: BLACK[100],
  },
  closeButton: {
    color: PRIMARY_PURPLE[70],
    p: 0.5,
  },
  body: {
    overflow: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  sectionLabel: {
    fontFamily: PRIMARY_FONT[500],
    fontSize: '12px',
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    color: PRIMARY_PURPLE[70],
  },
  stepHeading: {
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
  },
  stepNumber: {
    width: 28,
    height: 28,
    borderRadius: '50%',
    backgroundColor: PRIMARY_PURPLE[70],
    color: BLACK[0],
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: PRIMARY_FONT[700],
    fontSize: '14px',
    flexShrink: 0,
  },
  stepTitle: {
    fontFamily: PRIMARY_FONT[700],
    fontSize: '16px',
    letterSpacing: '0.02em',
    textTransform: 'uppercase',
    color: BLACK[100],
  },
  choiceRow: {
    display: 'flex',
    flexDirection: { xs: 'column', sm: 'row' },
    gap: 1.5,
  },
  choice: {
    flex: 1,
    display: 'flex',
    alignItems: 'flex-start',
    gap: 1,
    border: `1px solid ${BLACK[20] || '#E6E6E6'}`,
    borderRadius: 2,
    p: 1.5,
    cursor: 'pointer',
  },
  choiceSelected: {
    borderColor: PRIMARY_PURPLE[70],
    backgroundColor: PRIMARY_PURPLE[20],
  },
  choiceTitle: {
    fontFamily: PRIMARY_FONT[700],
    fontSize: '14px',
    color: BLACK[100],
  },
  choiceHint: {
    fontFamily: PRIMARY_FONT[400],
    fontSize: '12px',
    color: BLACK[60],
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: 1,
    backgroundColor: PRIMARY_PURPLE[10],
    borderRadius: 2,
    p: 1.5,
  },
  listRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 2,
    backgroundColor: 'background.paper',
    borderRadius: 1,
    px: 1.5,
    py: 1,
  },
  userRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    border: `1px solid ${BLACK[20] || '#E6E6E6'}`,
    borderRadius: 2,
    px: 1,
    py: 0.5,
    cursor: 'pointer',
  },
  roleChip: {
    marginLeft: 'auto',
    flexShrink: 0,
    borderRadius: '999px',
    px: 1.25,
    py: 0.25,
    fontFamily: PRIMARY_FONT[500],
    fontSize: '11px',
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    lineHeight: '18px',
  },
  adminChip: {
    backgroundColor: PRIMARY_PURPLE[20],
    color: PRIMARY_PURPLE[90],
  },
  recruiterChip: {
    backgroundColor: '#E7F3FF',
    color: ACTION_BLUE.ACTION_BLUE,
  },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 1.5,
    mt: 2,
  },
  footerActions: {
    display: 'flex',
    gap: 1.5,
  },
};

const StepHeading = ({ step, label }) => (
  <Box sx={styles.stepHeading}>
    <Box sx={styles.stepNumber}>{step}</Box>
    <Typography sx={styles.stepTitle}>{label}</Typography>
  </Box>
);

StepHeading.propTypes = {
  step: PropTypes.number.isRequired,
  label: PropTypes.string.isRequired,
};

const ManageAssociationsModal = ({ isOpen = false, jobOrders = [], onClose, onCompleted }) => {
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const [target, setTarget] = useState(TARGET.JOB_ORDERS);
  const [searchTerm, setSearchTerm] = useState('');
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const requestIdRef = useRef(0);

  const corporations = useMemo(() => uniqueCorporations(jobOrders), [jobOrders]);
  const jobOrderCount = jobOrders.length;
  const corporationCount = corporations.length;
  const isJobOrderTarget = target === TARGET.JOB_ORDERS;
  const actionCount = isJobOrderTarget ? jobOrderCount : corporationCount;
  const actionsDisabled = !selectedUserId || submitting || actionCount === 0;

  const resetForm = useCallback(() => {
    requestIdRef.current += 1;
    setTarget(TARGET.JOB_ORDERS);
    setSearchTerm('');
    setUsers([]);
    setSelectedUserId(null);
    setUsersLoading(false);
    setHasSearched(false);
    setSubmitting(false);
  }, []);

  const loadUsers = useCallback(
    (term) => {
      const requestId = requestIdRef.current + 1;
      requestIdRef.current = requestId;
      setUsersLoading(true);
      asyncService({
        httpMethod: 'POST',
        route: 'v2/users/read',
        data: buildUserSearchBody(term),
        token: user?.token,
        refreshToken: user?.refreshToken,
        dispatch,
        onSuccess: (response) => {
          if (requestId !== requestIdRef.current) return;
          const documents = response?.data?.documents || [];
          setUsers(documents);
          setSelectedUserId((current) =>
            documents.some((candidate) => candidate._id === current) ? current : null
          );
          setUsersLoading(false);
          setHasSearched(true);
        },
        onError: (error) => {
          if (requestId !== requestIdRef.current) return;
          setUsers([]);
          setSelectedUserId(null);
          setUsersLoading(false);
          setHasSearched(true);
          dispatchErrors({ dispatch, responsePayload: error?.response });
        },
      });
    },
    [dispatch, user?.refreshToken, user?.token]
  );

  useEffect(() => {
    if (!isOpen) {
      resetForm();
      return undefined;
    }
    const handle = setTimeout(() => {
      loadUsers(searchTerm);
    }, USER_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [isOpen, loadUsers, resetForm, searchTerm]);

  const submit = (isAssociated) => {
    if (actionsDisabled) return;
    setSubmitting(true);
    const route = isJobOrderTarget
      ? 'job-orders/associate-with-user'
      : 'users/associate-with-corporation';
    const data = isJobOrderTarget
      ? {
          userId: selectedUserId,
          jobOrders: jobOrders.map((jobOrder) => jobOrder._id),
          isAssociated,
        }
      : {
          userId: selectedUserId,
          corporationIds: corporations.map((corporation) => corporation.id),
          isAssociated,
        };

    asyncService({
      httpMethod: 'POST',
      route,
      data,
      token: user?.token,
      refreshToken: user?.refreshToken,
      dispatch,
      onSuccess: () => {
        setSubmitting(false);
        dispatch(
          showSnackbar({
            message: isAssociated
              ? 'jobOrders.associations.success.associate'
              : 'jobOrders.associations.success.remove',
          })
        );
        onCompleted();
      },
      onError: (error) => {
        setSubmitting(false);
        dispatchErrors({ dispatch, responsePayload: error?.response });
      },
    });
  };

  const associateLabel = LanguageConverterV2(
    isJobOrderTarget
      ? 'jobOrders.associations.associate.jobOrders'
      : 'jobOrders.associations.associate.corporations',
    { count: actionCount }
  );
  const removeLabel = LanguageConverterV2(
    isJobOrderTarget
      ? 'jobOrders.associations.remove.jobOrders'
      : 'jobOrders.associations.remove.corporations',
    { count: actionCount }
  );

  return (
    <Modal open={isOpen} onClose={onClose} aria-labelledby="manage-associations-title">
      <Box sx={styles.modal} data-testid="manage-associations-modal">
        <Box sx={styles.header}>
          <Typography id="manage-associations-title" sx={styles.title}>
            {LanguageConverterV2('jobOrders.associations.title')}
          </Typography>
          <IconButton
            onClick={onClose}
            sx={styles.closeButton}
            data-testid="manage-associations-close"
            aria-label={LanguageConverter('button.close')}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={styles.body}>
          <StepHeading
            step={1}
            label={LanguageConverterV2('jobOrders.associations.target.prompt')}
          />
          <RadioGroup
            value={target}
            onChange={(event) => setTarget(event.target.value)}
            sx={{
              ...styles.choiceRow,
              '&.MuiFormGroup-root': {
                flexDirection: { xs: 'column', sm: 'row' },
              },
            }}
          >
            <Box
              component="label"
              sx={{
                ...styles.choice,
                ...(isJobOrderTarget ? styles.choiceSelected : {}),
              }}
            >
              <Radio
                value={TARGET.JOB_ORDERS}
                inputProps={{ 'data-testid': 'target-job-orders' }}
              />
              <Box>
                <Typography sx={styles.choiceTitle}>
                  {LanguageConverterV2('jobOrders.associations.target.jobOrders', {
                    count: jobOrderCount,
                  })}
                </Typography>
                <Typography sx={styles.choiceHint}>
                  {LanguageConverterV2('jobOrders.associations.target.jobOrders.hint')}
                </Typography>
              </Box>
            </Box>
            <Box
              component="label"
              sx={{
                ...styles.choice,
                ...(!isJobOrderTarget ? styles.choiceSelected : {}),
              }}
            >
              <Radio
                value={TARGET.CORPORATIONS}
                inputProps={{ 'data-testid': 'target-corporations' }}
              />
              <Box>
                <Typography sx={styles.choiceTitle}>
                  {LanguageConverterV2('jobOrders.associations.target.corporations', {
                    count: corporationCount,
                  })}
                </Typography>
                <Typography sx={styles.choiceHint}>
                  {LanguageConverterV2('jobOrders.associations.target.corporations.hint')}
                </Typography>
              </Box>
            </Box>
          </RadioGroup>

          <Box sx={styles.list}>
            <Typography sx={styles.sectionLabel}>
              {LanguageConverterV2(
                isJobOrderTarget
                  ? 'jobOrders.associations.applying.jobOrders'
                  : 'jobOrders.associations.applying.corporations',
                { count: actionCount }
              )}
            </Typography>
            {isJobOrderTarget
              ? jobOrders.map((jobOrder) => (
                  <Box key={jobOrder._id} sx={styles.listRow}>
                    <Box>
                      <Typography sx={styles.choiceTitle}>{jobOrder.name}</Typography>
                      <Typography sx={styles.choiceHint}>{jobOrder.corporation?.name}</Typography>
                    </Box>
                    <Typography sx={styles.choiceHint}>
                      {`${epochToDateInReadableFormat(
                        jobOrder.start
                      )} ${epochToTimeInReadableFormat(jobOrder.start)}`}
                    </Typography>
                  </Box>
                ))
              : corporations.map((corporation) => (
                  <Box key={corporation.id} sx={styles.listRow}>
                    <Typography sx={styles.choiceTitle}>{corporation.name}</Typography>
                  </Box>
                ))}
          </Box>

          <StepHeading step={2} label={LanguageConverterV2('jobOrders.associations.who')} />
          <TextField
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder={LanguageConverterV2('jobOrders.associations.searchPlaceholder')}
            size="small"
            fullWidth
            inputProps={{
              'aria-label': LanguageConverterV2('jobOrders.associations.searchPlaceholder'),
              'data-testid': 'manage-associations-search',
            }}
          />
          {usersLoading && <CircularProgress size={20} />}
          {hasSearched && !usersLoading && users.length === 0 && (
            <Typography sx={styles.choiceHint}>
              {LanguageConverterV2('jobOrders.associations.noUsers')}
            </Typography>
          )}
          <RadioGroup
            value={selectedUserId || ''}
            onChange={(event) => setSelectedUserId(event.target.value)}
          >
            {users.map((candidate) => {
              const isAdminRole = candidate.role === UserRole.ADMIN;
              const roleKey = isAdminRole
                ? 'jobOrders.associations.role.admin'
                : 'jobOrders.associations.role.recruiter';
              const agencyName = candidate.recruiter?.agency?.name;
              return (
                <Box
                  key={candidate._id}
                  component="label"
                  sx={{
                    ...styles.userRow,
                    ...(selectedUserId === candidate._id ? styles.choiceSelected : {}),
                    mb: 1,
                  }}
                >
                  <Radio
                    value={candidate._id}
                    inputProps={{
                      'aria-label': LanguageConverterV2('jobOrders.associations.selectUser', {
                        name: candidate.name,
                      }),
                    }}
                  />
                  <Box>
                    <Typography sx={styles.choiceTitle}>{candidate.name}</Typography>
                    {agencyName ? (
                      <Typography sx={styles.choiceHint}>{agencyName}</Typography>
                    ) : null}
                  </Box>
                  <Typography
                    sx={{
                      ...styles.roleChip,
                      ...(isAdminRole ? styles.adminChip : styles.recruiterChip),
                    }}
                    data-testid={isAdminRole ? 'role-badge-admin' : 'role-badge-recruiter'}
                  >
                    {LanguageConverterV2(roleKey)}
                  </Typography>
                </Box>
              );
            })}
          </RadioGroup>
        </Box>

        <Box sx={styles.footer}>
          <WaeButton
            variant={BUTTON_VARIANT.OUTLINED}
            onClick={onClose}
            dataTestId="manage-associations-cancel"
          >
            {LanguageConverter('button.cancel')}
          </WaeButton>
          <Box sx={styles.footerActions}>
            <WaeButton
              variant={BUTTON_VARIANT.DELETE}
              onClick={() => submit(false)}
              disabled={actionsDisabled}
              dataTestId="manage-associations-remove"
            >
              {removeLabel}
            </WaeButton>
            <WaeButton
              variant={BUTTON_VARIANT.DEFAULT}
              onClick={() => submit(true)}
              disabled={actionsDisabled}
              dataTestId="manage-associations-associate"
            >
              {associateLabel}
            </WaeButton>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

ManageAssociationsModal.propTypes = {
  isOpen: PropTypes.bool,
  jobOrders: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string,
      name: PropTypes.string,
      start: PropTypes.number,
      corporation: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
          _id: PropTypes.string,
          id: PropTypes.string,
          name: PropTypes.string,
        }),
      ]),
    })
  ),
  onClose: PropTypes.func.isRequired,
  onCompleted: PropTypes.func.isRequired,
};

export default ManageAssociationsModal;
